/**
 * A published portal theme's three colours, grown into the page's token set.
 *
 * The operator publishes ground (60), ink (30) and accent (10); radportal sends
 * them with a server-derived mode. This module turns those three into every
 * variable portal.css reads, so the whole page -- sheet, text, borders, stage,
 * tiles, link, button -- follows the brand from one function.
 *
 * 🔑 EVERY TEXT PAIR IS MEASURED, NOT ASSUMED. The operator chooses colours for
 * their brand, not for contrast, and the same gold that fails as text on white
 * (2.06:1) passes on navy (7.73:1). Each text colour starts from the brand and
 * is moved toward black or white only until it clears its WCAG floor, so a
 * colour that already passes is used exactly as published.
 *
 * Design: radius-wiki portal-brand-from-logo.md. The mode rule is shared with
 * radhotspotapi (testdata/portal-ground-mode.json); change it in both or neither.
 */

export type Mode = "light" | "dark";
type RGB = [number, number, number];

const HEX = /^#[0-9a-f]{6}$/i;

export function parseHex(hex: string): RGB | null {
  if (!HEX.test(hex)) return null;
  return [0, 2, 4].map((i) => parseInt(hex.slice(1 + i, 3 + i), 16)) as RGB;
}

function toHex(c: RGB): string {
  return "#" + c.map((v) => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, "0")).join("").toUpperCase();
}

function linear(v: number): number {
  const s = v / 255;
  return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

/** WCAG relative luminance, 0 (black) to 1 (white). */
export function luminance(hex: string): number {
  const c = parseHex(hex);
  if (!c) return 0;
  return 0.2126 * linear(c[0]) + 0.7152 * linear(c[1]) + 0.0722 * linear(c[2]);
}

/** WCAG contrast ratio, 1 to 21. */
export function contrast(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi! + 0.05) / (lo! + 0.05);
}

/** The luminance at which white and black text have equal contrast. */
export const DARK_GROUND_BELOW = Math.sqrt(1.05 * 0.05) - 0.05;

/** radhotspotapi's rule: a ground darker than the crossover is a dark page. */
export function modeForGround(ground: string): Mode {
  return luminance(ground) < DARK_GROUND_BELOW ? "dark" : "light";
}

/** a moved toward b by t (0..1), in sRGB. */
export function mix(a: string, b: string, t: number): string {
  const x = parseHex(a)!;
  const y = parseHex(b)!;
  return toHex([0, 1, 2].map((i) => x[i]! + (y[i]! - x[i]!) * t) as RGB);
}

/**
 * fg itself when it clears `floor` on bg; otherwise fg moved, in 5% steps,
 * toward whichever of black and white contrasts more with bg. Black or white
 * always reaches 4.58:1 against any colour, so every floor up to that is met.
 */
export function readable(fg: string, bg: string, floor: number): string {
  if (contrast(fg, bg) >= floor) return fg;
  const pole = contrast("#000000", bg) >= contrast("#FFFFFF", bg) ? "#000000" : "#FFFFFF";
  for (let i = 1; i <= 20; i++) {
    const c = mix(fg, pole, i / 20);
    if (contrast(c, bg) >= floor) return c;
  }
  return pole;
}

/** Every variable deriveTokens sets. applyTheme removes exactly these when no theme is published. */
export const THEME_TOKENS = [
  "--bg", "--surface", "--surface-2", "--border", "--border-strong",
  "--text", "--text-2", "--text-3", "--link", "--accent-soft", "--on-accent",
  "--head-bg", "--head-fg", "--head-sub", "--tile-bg", "--tile-fg",
  "--stage", "--stage-fg", "--stage-sub", "--stage-rgb",
] as const;

export type ThemeTokens = Record<(typeof THEME_TOKENS)[number], string>;

/**
 * The token set for a published theme, or null when any colour is not exactly
 * six hex digits (radportal validates too; this must not be the only guard,
 * and it is not the first).
 */
export function deriveTokens(ground: string, ink: string, accent: string, mode: Mode): ThemeTokens | null {
  if (!parseHex(ground) || !parseHex(ink) || !parseHex(accent)) return null;
  const dark = mode === "dark";

  // 60: the ground is the page; cards sit on white in a light page and on a
  // slightly lifted ground in a dark one.
  const surface = dark ? mix(ground, ink, 0.08) : "#FFFFFF";
  // 30: the ink writes. Secondary text is the ink faded toward the surface,
  // pulled back only as far as its floor requires.
  const text = readable(ink, surface, 4.5);
  const text2 = readable(mix(text, surface, 0.35), surface, 4.5);
  const text3 = readable(mix(text, surface, 0.55), surface, 3);

  // The stage band is the darker of the two brand colours, written in the lighter.
  const [stage, stageInk] = luminance(ground) <= luminance(ink) ? [ground, ink] : [ink, ground];
  const stageFg = readable(stageInk, stage, 4.5);
  const stageSub = readable(mix(stageFg, stage, 0.3), stage, 4.5);

  // 10: the accent FILLS. As text it is used raw only where it passes.
  const link = readable(accent, surface, 4.5);
  const onAccent = [stage, stageFg, "#000000", "#FFFFFF"].reduce((best, c) =>
    contrast(c, accent) > contrast(best, accent) ? c : best,
  );

  const [r, g, b] = parseHex(stage)!;
  return {
    "--bg": ground,
    "--surface": surface,
    "--surface-2": mix(surface, text, dark ? 0.08 : 0.05),
    "--border": mix(surface, text, 0.12),
    "--border-strong": mix(surface, text, 0.22),
    "--text": text,
    "--text-2": text2,
    "--text-3": text3,
    "--link": link,
    "--accent-soft": mix(surface, accent, dark ? 0.2 : 0.18),
    "--on-accent": onAccent,
    "--head-bg": stage,
    "--head-fg": stageFg,
    "--head-sub": stageSub,
    "--tile-bg": stage,
    "--tile-fg": stageFg,
    "--stage": stage,
    "--stage-fg": stageFg,
    "--stage-sub": stageSub,
    "--stage-rgb": `${r}, ${g}, ${b}`,
  };
}
